<template>
  <div class="compton-pro-fixed">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$Δλ = (h/m_e c)(1 - cosθ)$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>入射波长 λ</label>
            <span class="val">0.050 <small>Å</small></span>
          </div>
          <div class="hud-item orange">
            <label>散射波长 λ'</label>
            <span class="val">{{ scatteredLambda.toFixed(4) }} <small>Å</small></span>
          </div>
          <div class="hud-item green">
            <label>波长偏移 Δλ</label>
            <span class="val">+{{ (scatteredLambda - 0.05).toFixed(4) }} <small>Å</small></span>
          </div>
        </div>

        <svg width="100%" height="260" viewBox="0 0 450 260" preserveAspectRatio="xMidYMid meet">
          <defs>
             <filter id="glow-wave"><feGaussianBlur stdDeviation="3" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
             <marker id="arr-e" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 Z" fill="#0ea5e9" /></marker>
          </defs>

          <!-- 碰撞中心 -->
          <circle cx="200" cy="130" r="2" fill="#334155" />

          <!-- A. 碰撞前: 入射高能光子 (短波-蓝紫) -->
          <g v-if="phase === 'incident' || phase === 'collide'">
             <path :d="photonInPath" fill="none" stroke="#a855f7" stroke-width="2.5" filter="url(#glow-wave)">
                <animate attributeName="stroke-dasharray" from="0,200" to="200,0" dur="0.8s" repeatCount="indefinite" />
             </path>
             <text x="60" y="115" fill="#a855f7" font-size="10" font-weight="900">入射 X 射线 (高能)</text>
          </g>

          <!-- B. 静止/反冲电子 -->
          <g :transform="`translate(${electron.x}, ${electron.y})`">
             <circle r="8" fill="#0ea5e9" filter="url(#glow-wave)" />
             <text y="20" text-anchor="middle" fill="#0ea5e9" font-size="10" font-weight="900">电子 m_e</text>
             <!-- 速度矢量 -->
             <line v-if="phase === 'scatter'" x1="0" y1="0" x2="30" y2="0" stroke="#0ea5e9" stroke-width="2" marker-end="url(#arr-e)" />
          </g>

          <!-- C. 碰撞后: 散射光子 (红移-波长变长) -->
          <g v-if="phase === 'scatter'">
             <path :d="photonOutPath" fill="none" :stroke="scatterColor" stroke-width="2" stroke-dasharray="4,2">
                <animate attributeName="stroke-dashoffset" from="24" to="0" dur="0.2s" repeatCount="indefinite" />
             </path>
             <text :x="200 + Math.cos(thetaRad)*100" :y="130 - Math.sin(thetaRad)*100" :fill="scatterColor" font-size="10" font-weight="900">散射光子 λ'</text>
             
             <!-- 角度标尺 θ -->
             <path :d="angleArc" fill="none" stroke="#475569" stroke-dasharray="2,2" />
             <text :x="250" :y="130 - Math.sin(thetaRad/2)*40" fill="#475569" font-size="9">θ = {{ theta }}°</text>
          </g>
        </svg>

        <div class="compton-viewer">
           <div class="label">能量守恒分析 (Energy Balance)</div>
           <div class="bars">
              <div class="bar-item">
                 <span>光子能量损失</span>
                 <div class="track"><div class="fill" :style="{ width: energyLossRatio + '%', background: '#f43f5e' }"></div></div>
              </div>
              <div class="bar-item">
                 <span>电子获得动能</span>
                 <div class="track"><div class="fill" :style="{ width: energyLossRatio + '%', background: '#22c55e' }"></div></div>
              </div>
           </div>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex-row items-center gap-6">
              <div class="flex-1">
                 <label class="s-label">调节散射探测角 (θ)</label>
                 <el-slider v-model="theta" :min="0" :max="180" @change="reset"></el-slider>
              </div>
              <div class="btn-group">
                 <el-button type="primary" size="small" @click="start">开始粒子碰撞</el-button>
                 <el-button size="small" @click="reset">复位</el-button>
              </div>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-aim"></i>
           <p><strong>实战物理：</strong>Compton 效应证明了光子像“台球”一样具有粒子动量。<strong>注意颜色：</strong>随着散射角 $\theta$ 增大，散射光子从紫色变为红色，这代表波长在变长（能量损失）。多出的能量被电子带走了（反冲）。调节至 180° 进行“对头碰”，你会看到波长位移达到最大值。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'ComptonEffectViz',
  data() {
    return {
      theta: 45,
      phase: 'incident', // incident, collide, scatter
      electron: { x: 200, y: 130 }
    }
  },
  computed: {
    thetaRad() { return (this.theta * Math.PI) / 180; },
    scatteredLambda() {
       // Delta_lambda = 0.0242 * (1 - cos theta)
       return 0.05 + 0.02426 * (1 - Math.cos(this.thetaRad));
    },
    scatterColor() {
       const shift = this.scatteredLambda - 0.05;
       if (shift < 0.005) return '#8b5cf6'; // Violet
       if (shift < 0.015) return '#3b82f6'; // Blue
       if (shift < 0.030) return '#eab308'; // Yellow
       return '#ef4444'; // Red shift
    },
    photonInPath() { return `M 20,130 L 200,130`; },
    photonOutPath() {
       const lx = 200 + Math.cos(this.thetaRad) * 160;
       const ly = 130 - Math.sin(this.thetaRad) * 160;
       return `M 200,130 L ${lx},${ly}`;
    },
    angleArc() {
       const endX = 200 + Math.cos(this.thetaRad) * 40;
       const endY = 130 - Math.sin(this.thetaRad) * 40;
       return `M 240,130 A 40,40 0 0 1 ${endX},${endY}`;
    },
    energyLossRatio() {
       // E = hc/lambda. loss = (1/L_in - 1/L_out) / (1/L_in) = 1 - L_in/L_out
       return (1 - 0.05 / this.scatteredLambda) * 300; // Scaling for visual
    }
  },
  methods: {
    start() {
       this.phase = 'collide';
       setTimeout(() => {
          this.phase = 'scatter';
          // Move electron based on conservation (simplified momentum)
          const recoilPhi = Math.atan2(Math.sin(this.thetaRad), (0.05 / 0.024) + 1 - Math.cos(this.thetaRad));
          this.animateElectron();
       }, 600);
    },
    reset() {
       this.phase = 'incident';
       this.electron = { x: 200, y: 130 };
       cancelAnimationFrame(this.animFrame);
    },
    animateElectron() {
       const phi = Math.atan2(Math.sin(this.thetaRad), 1.5 - Math.cos(this.thetaRad)); 
       let start = Date.now();
       const move = () => {
          const dt = Date.now() - start;
          if (dt < 1000) {
             this.electron.x = 200 + (dt/1000) * 150 * Math.cos(-phi);
             this.electron.y = 130 + (dt/1000) * 150 * Math.sin(-phi);
             this.animFrame = requestAnimationFrame(move);
          }
       };
       move();
    }
  },
  beforeDestroy() {
    if (this.animFrame) cancelAnimationFrame(this.animFrame);
    if (this.timer) clearInterval(this.timer);
  },
}
</script>

<style scoped lang="scss">
.compton-pro-fixed { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 2px 4px; .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; } .formula-chip { background: #fef2f2; color: #991b1b; border: 1px solid #fecdd3; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { min-height: 260px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 8px; border: 1px solid #334155; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 13px; color: #f8fafc; font-weight: 800; } &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #fbbf24; } &.green { border-left: 3px solid #22c55e; } } }
.compton-viewer { position: absolute; bottom: 12px; right: 12px; width: 140px; background: rgba(15,23,42,0.8); padding: 8px; border-radius: 6px; .label { font-size: 9px; color: #64748b; font-weight: 900; margin-bottom: 6px; } .bar-item { margin-bottom: 4px; span { display: block; font-size: 8px; color: #94a3b8; } .track { height: 4px; background: #1e293b; border-radius: 2px; overflow: hidden; .fill { height: 100%; transition: width 0.3s; } } } }
.compact-controls { display: flex; flex-direction: column; gap: 6px; }
.control-card { background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0; }
.s-label { font-size: 11px; color: #64748b; font-weight: 800; margin-bottom: 2px; display: block; }
.flex { display: flex; } .items-center { align-items: center; } .flex-row { flex-direction: row; } .flex-1 { flex: 1; } .gap-6 { gap: 24px; }
.discovery-card-compact { background: #fffcf0; padding: 10px 14px; border-radius: 10px; border: 1px solid #fef9c3; display: flex; align-items: center; gap: 10px; i { color: #ca8a04; font-size: 1.2rem; } p { flex: 1; margin: 0; font-size: 12px; color: #854d0e; line-height: 1.5; strong { color: #713f12; } } }
</style>
